import React, { useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { createPortal } from 'react-dom'
import { BlobProvider } from '@react-pdf/renderer'
import { useOrganization } from 'contexts/organization'
import { useSmartEntity, useExportSmartEntity } from 'contexts/smart-entity'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { useDocuments } from 'contexts/documents'
import { useAccess } from 'contexts/access'
import { useUpgradable } from 'hooks/upgradable'
import { sendAppSignal } from 'hooks/app-signal'
import paths from 'app/paths'
import { saveAs } from 'file-saver'
import { getSmartDocumentUrl } from 'utilities/url'
import { ContextMenu } from '../../s'
import Ellipsify from 'components/ellipsify'
import PDFContent from 'components/tiptap/output/pdf'
import { Scrollable as Modal } from 'modals/generic'
import UploadDocument from 'pages/documents/modals/add-document/file'
import {
    FilePlus as GeneratePdf,
    Edit,
    Download,
    DownloadCloud as ExportAsTemplate,
    Link as CopyLink,
    Trash2 as Delete
} from 'styled-icons/feather'
import {
    SettingsDocument,
    Signature
} from 'components/feather'

const PreviewSmartDocumentDetailsActions = ({ listDokument = null, setEditing, setConfiguringSigning, viewContext = 'modal', slim = false, includeLogo = false, dismiss, salt }) => {
    const { formatMessage } = useIntl()

    const { organization = {} } = useOrganization()

    const {
        entity,
        interpolate,
        getVariableData
    } = useSmartEntity()

    const { removeDocument } = useDocuments()
    const exportAsTemplate = useExportSmartEntity()


    const dokument = entity?.id ?
        entity :
        (listDokument ?? {})

    const {
        check,
        checkFeature
    } = useAccess()

    const checkUpgradable = useUpgradable()

    const signingAvailable = checkFeature('digital-signing')
    const signingUpgradable = checkUpgradable({ feature: 'digital-signing' })

    const { editable, signable } = useCheckDocumentPermission()(dokument)

    const smartDocumentsAvailable = checkFeature('smart-documents')
    const smartDocumentsUpgradable = checkUpgradable({ feature: 'smart-documents' })

    const smartTemplatesAccess = check('smart-document-templates:manage')
    const smartTemplatesImportExportAvailable = checkFeature('smart-document-templates-import-export')
    const smartTemplatesImportExportUpgradable = checkUpgradable({ feature: 'smart-document-templates-import-export' })

    const customLogoAccess = checkFeature('company-logo')

    const [uploading, setUploading] = useState(null)
    const [loading, setLoading] = useState(null)
    const [destructing, setDestructing] = useState(false)
    const [pdfPortal, setPdfPortal] = useState(null)

    const generatePdf = useCallback(async which => {
        setLoading(which)

        const result = await new Promise((resolve, reject) => void setPdfPortal(
            createPortal((
                <BlobProvider document={(
                    <PDFContent
                        {...dokument}
                        interpolate={interpolate}
                        getVariableData={getVariableData}
                        logo={(includeLogo && customLogoAccess) && organization?.companyLogo}
                        salt="smart" />
                )}>
                    {({ blob, error }) => {
                        if(blob) {
                            resolve(blob)
                        }

                        if(error) {
                            reject()
                        }

                        return null
                    }}
                </BlobProvider>
            ), document.getElementById('off-screen'))
        ))

        setPdfPortal(null)
        setLoading(null)

        return result
    }, [interpolate, includeLogo, customLogoAccess, organization?.companyLogo])

    const downloadPdf = async () => {
        const blob = await generatePdf('download')
        saveAs(blob, `${dokument.name}.pdf`)
    }

    const uploadPdf = async (signing = true) => {
        const blob = await generatePdf(signing ? 'signing' : 'generate')

        setUploading({
            ...dokument,
            signing,
            setConfiguringSigning,
            file: {
                blob,
                name: dokument.name
            }
        })
    }

    const actions = {
        configureSigning: () => {
            if(!signingAvailable && !signingUpgradable) {
                return null
            }

            if(!signable) {
                return null
            }

            return {
                salt: `${salt}:configure-signing`,
                icon: <Signature size={24} />,
                label: formatMessage({
                    id: 'document_action_send_for_signing',
                    defaultMessage: 'Send for signing'
                }),
                effect: 'neutral',
                disabled: loading === 'signing' || !!listDokument,
                onClick: () => uploadPdf(),
                ...(signingUpgradable ? {
                    upgrade: {
                        feature: 'digital-signing'
                    }
                } : null)
            }
        },
        storeAsPdfDocument: () => {
            if(!editable) {
                return null
            }

            return {
                salt: `${salt}:generate-pdf`,
                icon: <GeneratePdf size={24} />,
                label: formatMessage({
                    id: 'document_action_generate_pdf',
                    defaultMessage: 'Store as PDF document'
                }),
                effect: 'neutral',
                disabled: loading === 'generate' || !!listDokument,
                onClick: () => uploadPdf(false)
            }
        },
        editDocument: () => {
            if(!smartDocumentsAvailable && !smartDocumentsUpgradable) {
                return null
            }

            if(!editable || viewContext === 'page' || slim) {
                return null
            }

            return {
                salt: `${salt}:editor`,
                icon: <Edit size={24} />,
                label: formatMessage({
                    id: 'document_action_edit',
                    defaultMessage: 'Edit document'
                }),
                element: 'link',
                link: { to: getSmartDocumentUrl(dokument) },
                effect: 'neutral',
                ...(smartDocumentsUpgradable ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        },
        settings: () => {
            if(!smartDocumentsAvailable && !smartDocumentsUpgradable) {
                return null
            }

            if(!editable || slim) {
                return null
            }

            return {
                salt: `${salt}:settings`,
                icon: <SettingsDocument size={24} />,
                label: formatMessage({
                    id: 'document_action_settings',
                    defaultMessage: 'Document settings'
                }),
                effect: 'neutral',
                disabled: !!listDokument,
                onClick: () => setEditing('auto'),
                ...(smartDocumentsUpgradable ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        },
        download: () => {
            if(!smartDocumentsAvailable && !smartDocumentsUpgradable) {
                return null
            }

            return {
                salt: `${salt}:download`,
                icon: <Download size={24} />,
                label: formatMessage({
                    id: 'action_download',
                    defaultMessage: 'Download'
                }),
                effect: 'neutral',
                disabled: loading === 'download' || !!listDokument,
                onClick: downloadPdf,
                ...(smartDocumentsUpgradable ? {
                    upgrade: {
                        feature: 'smart-documents'
                    }
                } : null)
            }
        },
        exportAsTemplate: () => {
            if(!smartTemplatesAccess) {
                return null
            }

            if(!smartTemplatesImportExportAvailable && !smartTemplatesImportExportUpgradable) {
                return null
            }

            return {
                salt: `${salt}:export-as-template`,
                icon: <ExportAsTemplate size={24} />,
                label: formatMessage({
                    id: 'documents_smart_action_export_as_template',
                    defaultMessage: 'Export as template'
                }),
                effect: 'neutral',
                onClick: () => exportAsTemplate(dokument, 'document'),
                ...(smartTemplatesImportExportUpgradable ? {
                    upgrade: {
                        feature: 'smart-document-templates-import-export'
                    }
                } : null)
            }
        },
        copyLink: () => ({
            salt: `${salt}:copy-link`,
            icon: <CopyLink size={24} />,
            label: formatMessage({
                id: 'action_copy_link',
                defaultMessage: 'Copy link'
            }),
            effect: 'neutral',
            onClick: () => global.navigator.clipboard.writeText(`${window.location.origin}${paths.documents.base}?id=${dokument.id}`)
        }),
        remove: () => {
            if(!editable || slim) {
                return null
            }

            return {
                salt: `${salt}:delete`,
                icon: <Delete size={24} />,
                label: formatMessage({
                    id: 'action_delete',
                    defaultMessage: 'Delete'
                }),
                effect: 'destructive',
                loading: destructing,
                onClick: async () => {
                    setDestructing(true)
                    const { ok } = await removeDocument(dokument.id)

                    if(ok) {
                        dismiss?.()
                    } else {
                        setDestructing(false)
                    }
                },
                prompt: {
                    question: formatMessage({
                        id: 'document_action_delete_prompt_question',
                        defaultMessage: 'Confirm deleting {document}'
                    }, {
                        document: (
                            <Ellipsify
                                text={`${dokument.name}.smart`}
                                head={9}
                                tail={9}
                                tooltipProps={{ maxWidth: 'none' }} />
                        )
                    }),
                    confirmText: formatMessage({
                        id: 'action_delete',
                        defaultMessage: 'Delete'
                    })
                }
            }
        }
    }

    return (
        <>
            <ContextMenu
                actions={actions}
                context={dokument}
                salt={salt} />
            <Modal
                show={!!uploading}
                dismiss={() => setUploading(null)}
                salt={`${salt}:upload`}>
                <UploadDocument
                    {...uploading}
                    salt={`${salt}:upload`}
                    onDone={() => sendAppSignal('smart-document:references:update', dokument.id)} />
            </Modal>
            {pdfPortal}
        </>
    )
}

export default PreviewSmartDocumentDetailsActions